import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import config from 'config/config';
import Analytics from 'utils/Analytics';
import checkOrSetSlash from 'utils/checkOrSetSlash';
import { locationType } from 'utils/constants';
import { prepareFCHeader } from 'utils/request';

const rootProjectPath = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/`;

export const plansApi = createApi({
  reducerPath: 'plansApi',
  baseQuery: fetchBaseQuery({
    baseUrl: rootProjectPath,
    prepareHeaders: prepareFCHeader(),
  }),
  tagTypes: ['Plans'],
  endpoints: builder => ({
    fetchPlans: builder.query({
      query: ({ projectId, folderId, limit, offset, search_term }) => ({
        url: `projects/${projectId}/folder-content?limit=${limit}&offset=${offset}&search_name=${search_term}`,
        method: 'GET',
        params: folderId ? { folder_id: folderId } : undefined,
      }),
      transformResponse: response => ({
        ...response,
        contents: response.contents?.map(item => ({ ...item, __reorder__: item.name })),
      }),
      providesTags: ['Plans'],
    }),
    fetchAllPlans: builder.query({
      query: ({ projectId }) => ({
        url: `projects/${projectId}/plans`,
        method: 'GET',
      }),
      providesTags: ['Plans'],
    }),
    fetchDataItem: builder.mutation({
      query: ({ params }) => ({
        url: `projects/${params.idData}/folders/${params.itemId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Plans'],
    }),
    saveItem: builder.mutation({
      query: ({ projectId, item, itemId }) => {
        const isPlan = item.type === locationType.plan || item.type === locationType.bim;
        const query = isPlan ? `/plans/${item.id}` : `/folders/${item.id || itemId}`;

        return {
          url: `projects/${projectId}${query}`,
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'No-Store',
          },
          body: JSON.stringify(item),
        };
      },
      invalidatesTags: ['Plans'],
    }),
    deleteItem: builder.mutation({
      query: ({ projectId, item }) => {
        const isPlan = item.type === locationType.plan || item.type === locationType.bim;
        const query = isPlan ? `/plans/${item.id}` : `/folders/${item.id}`;
        return {
          url: `projects/${projectId}${query}`,
          method: 'DELETE',
        };
      },
      transformResponse: response => {
        Analytics.track('plans_plan_delete_succeed');
        return response;
      },
      invalidatesTags: ['Plans'],
    }),
    saveOrderedItem: builder.mutation({
      query: ({ projectId, folderId, item }) => {
        const isPlan = item.type === locationType.plan || item.type === locationType.bim;
        const query = isPlan ? `/plans/${item.id}` : `/folders/${item.id}`;
        const rootMediaResourceId = folderId === undefined ? item.media_resource?.id : folderId;

        let thumbnail = null;
        if (item.thumbnail_resource_id) {
          thumbnail = {
            media_resource_id: item.thumbnail_resource_id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          };
        }

        const payload = {
          ...item,
          media_resource: {
            media_resource_id: item.media_resource_id || item.media_resource?.id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          },
          thumbnail,
          plan_update_model: 'null',
          folder_id: folderId,
          order: item.order,
        };

        return {
          url: `projects/${projectId}${query}`,
          method: 'PUT',
          headers: {
            'Cache-Control': 'No-Store',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        };
      },
      transformResponse: (response, meta, arg) => {
        if (arg?.item?.type === locationType.folder) {
          Analytics.track('Plan_folder_reorder_succeed');
        } else {
          Analytics.track('Plan_plan_reorder_succeed');
        }
        return response;
      },
      invalidatesTags: ['Plans'],
    }),
    renameItem: builder.mutation({
      query: ({ projectId, folderId, item }) => {
        const rootMediaResourceId = folderId === undefined ? item.media_resource?.id : folderId;

        let thumbnail = null;
        if (item.thumbnail_resource_id) {
          thumbnail = {
            media_resource_id: item.thumbnail_resource_id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          };
        }

        const payload = {
          ...item,
          media_resource: {
            media_resource_id: item.media_resource_id || item.media_resource?.id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          },
          thumbnail,
          plan_update_model: 'null',
          folder_id: folderId,
          name: item.name,
        };

        const optionPlan = {
          url: `v1.3/projects/${projectId}/plans/${item.id}`,
          method: 'PUT',
          headers: {
            'Cache-Control': 'No-Store',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        };
        const options = {
          url: `v1.3/projects/${projectId}/folders/${item.id}`,
          method: 'PUT',
          headers: {
            'Cache-Control': 'No-Store',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...item, parent_folder_id: folderId }),
        };

        return item.type === locationType.plan ? optionPlan : options;
      },
      invalidatesTags: ['Plans'],
    }),
    deleteImage: builder.mutation({
      query: ({ projectId, imageId }) => ({
        url: `projects/${projectId}/folders/${imageId}/detach-media`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          detach_media_resource_ids: [imageId],
        }),
      }),
      invalidatesTags: ['Plans'],
    }),
    postFolder: builder.mutation({
      query: ({ projectId, values, parentFolderId }) => ({
        url: `projects/${projectId}/folders`,
        method: 'POST',
        headers: {
          'Cache-Control': 'No-Store',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          id: values.id,
          parent_folder_id: parentFolderId,
        }),
      }),
      transformResponse: response => {
        Analytics.track('plans_folder_creation_succeeded');
        return response;
      },
      invalidatesTags: ['Plans'],
    }),
  }),
});

export const {
  useFetchPlansQuery,
  useFetchAllPlansQuery,
  useFetchDataItemMutation,
  useSaveItemMutation,
  useDeleteItemMutation,
  useSaveOrderedItemMutation,
  useRenameItemMutation,
  useDeleteImageMutation,
  usePostFolderMutation,
} = plansApi;
